import { useMemo } from "react";
import type { AIInsight, EventTool } from "../lib/types";

export interface TickerSlot {
  tool: EventTool;
  insight: AIInsight;
}

/**
 * One slot per enabled tool that has at least one is_displayed insight for the
 * current question. Picks the most recent insight per tool by created_at.
 * Sort order follows event_tools.sort_order. Disabled tools are filtered out.
 * Legacy insights with tool_id === null are unreachable here by design — the
 * ticker is per-tool, so untagged insights have no slot to live in.
 */
export function useInsightTickerSlots(
  insights: AIInsight[],
  tools: EventTool[],
): TickerSlot[] {
  return useMemo(() => {
    const latestByToolId = new Map<string, AIInsight>();
    for (const insight of insights) {
      if (!insight.is_displayed) continue;
      if (!insight.tool_id) continue;
      const prev = latestByToolId.get(insight.tool_id);
      if (!prev || insight.created_at > prev.created_at) {
        latestByToolId.set(insight.tool_id, insight);
      }
    }

    const slots: TickerSlot[] = [];
    for (const tool of tools) {
      if (!tool.is_enabled) continue;
      const insight = latestByToolId.get(tool.id);
      if (insight) slots.push({ tool, insight });
    }
    slots.sort((a, b) => a.tool.sort_order - b.tool.sort_order);
    return slots;
  }, [insights, tools]);
}
